import {
  RoutineWorkflow,
  useUpdateMutation,
  WorkspaceAsset,
} from '@gripp/shared-logic';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Alert, ScrollView, Text, View } from 'react-native';
import { Colors } from '../../../themes/colors';
import { themedStyleSheet } from '../../../themes/themedStyleSheet';
import { Button } from '../../crop-circle/Button/Button';
import { TextInput as CropCircleTextInput } from '../../crop-circle/Components/TextInput/TextInput';
import { tokens } from '../../crop-circle/tokens';
import { fontWeights } from '../../crop-circle/tokens/typography';
import { AlertSummary } from './alertSummary';
import { AlertFormRef } from './types';

const UPDATE_WORKFLOW = `
  mutation UpdateWorkflow($input: MutateWorkflowInput!) {
    updateWorkflow(input: $input) {
      id
    }
  }
`;

type GroupAlertAssetFormProps = {
  workflow: RoutineWorkflow;
  workspaceAsset: WorkspaceAsset;
  routineId: string;
  onClose: () => void;
  formRef?: MutableRefObject<AlertFormRef>;
  renderBottomComponent?: (
    onSubmit: () => void,
    disabled: boolean
  ) => React.ReactNode;
  onRegisterBackGuard?: (
    guard: (onProceed: () => void, onCancel?: () => void) => void
  ) => void;
};

type StartingPointValues = { assetIntervalStart: string };

/**
 * A group alert as seen from one of its assets. The trigger belongs to the
 * group, so everything about it is read-only here and edited on the group
 * instead; the one asset-local value is where this asset's counter interval
 * starts counting from, which the summary's Reset also rewrites.
 */
export const GroupAlertAssetForm = ({
  workflow,
  workspaceAsset,
  routineId,
  onClose,
  formRef,
  renderBottomComponent,
  onRegisterBackGuard,
}: GroupAlertAssetFormProps) => {
  const { t } = useTranslation();
  const config = workflow.trigger?.config;
  const counterRoutineId = config?.triggerCounterRoutineId;
  const isCounterInterval = !!config?.onInterval && !!counterRoutineId;

  const { mutateAsync: updateWorkflow } = useUpdateMutation({
    modelName: 'workflow',
    query: UPDATE_WORKFLOW,
  });

  const currentCount = useMemo(() => {
    const routineState = workspaceAsset.routineState?.find(
      (state) => state.workspaceRoutineId === counterRoutineId
    );
    return Number(routineState?.state?.count) || 0;
  }, [workspaceAsset.routineState, counterRoutineId]);

  const storedStart = config?.assetIntervalStarts?.[workspaceAsset.id];
  const defaultValues: StartingPointValues = useMemo(
    () => ({
      assetIntervalStart: String(storedStart ?? currentCount),
    }),
    [storedStart, currentCount]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm<StartingPointValues>({ defaultValues });

  const startValue = useWatch({ control, name: 'assetIntervalStart' });

  const resetToDefaults = useCallback(
    () => reset(defaultValues),
    [reset, defaultValues]
  );

  // The back guard is registered once on mount, so it reads dirtiness from a
  // ref rather than the closure it captured.
  const isDirtyRef = useRef(isDirty);
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  // The summary's Reset writes this asset's anchor straight to the workflow, so
  // the stored value can change under an open form. Follow it while the field
  // is untouched; a value the user is editing wins.
  useEffect(() => {
    if (!isDirty) resetToDefaults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  useEffect(() => {
    onRegisterBackGuard?.((onProceed, onCancel) => {
      if (!isDirtyRef.current) {
        onProceed();
        return;
      }
      Alert.alert(
        t('common.unsavedChanges'),
        t('common.unsavedChangesMessage'),
        [
          {
            text: t('common.cancel'),
            style: 'cancel',
            onPress: () => onCancel?.(),
          },
          {
            text: t('common.discard'),
            style: 'destructive',
            onPress: () => onProceed(),
          },
        ]
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (!formRef) return;
    formRef.current = { handleSubmit, reset: resetToDefaults };
    return () => {
      formRef.current = null;
    };
  }, [formRef, handleSubmit, resetToDefaults]);

  const onSubmit = async ({ assetIntervalStart }: StartingPointValues) => {
    // Empty is not zero: an anchor of 0 makes the alert instantly overdue, so
    // a cleared field can't be saved (Confirm is disabled for it too).
    if (!config || !assetIntervalStart) return;

    await updateWorkflow({
      input: {
        id: workflow.id,
        trigger: {
          ...workflow.trigger,
          config: {
            ...config,
            assetIntervalStarts: {
              ...(config.assetIntervalStarts ?? {}),
              [workspaceAsset.id]: Number(assetIntervalStart),
            },
          },
        },
      },
    });
    onClose();
  };

  const confirmDisabled = isSubmitting || !isDirty || !startValue;

  return (
    <View style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <AlertSummary
          workflow={workflow}
          workspaceAsset={workspaceAsset}
          routineId={routineId}
          onAlertSelected={() => undefined}
          readOnly
        />
        <Text style={styles.note}>
          {t('alerts.managedOnGroupNote', {
            group: workflow.group?.name ?? '',
          })}
        </Text>

        {isCounterInterval && (
          <View style={styles.field}>
            <Text style={styles.fieldLabel}>{t('alerts.startingValue')}</Text>
            <Controller
              name="assetIntervalStart"
              control={control}
              render={({ field }) => (
                <CropCircleTextInput
                  keyboardType="numeric"
                  value={field.value}
                  onChangeText={(text) =>
                    field.onChange(text.replace(/[^0-9]/g, ''))
                  }
                  placeholder={String(currentCount)}
                  backgroundColor={Colors.role.surfaceHover}
                  hideBorder={true}
                />
              )}
            />
          </View>
        )}
      </ScrollView>

      {isCounterInterval && (
        <View style={styles.actions}>
          {renderBottomComponent ? (
            renderBottomComponent(handleSubmit(onSubmit), confirmDisabled)
          ) : (
            <Button
              state="active"
              onPress={handleSubmit(onSubmit)}
              disabled={confirmDisabled}
              style={styles.confirm}
            >
              {t('alerts.confirm')}
            </Button>
          )}
        </View>
      )}
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    flex: 1,
    backgroundColor: role.canvas,
  },
  scrollContent: {
    padding: tokens.spacing.lg,
  },
  note: {
    ...tokens.typography.body.small,
    color: role.textSecondary,
    marginTop: tokens.spacing.sm,
  },
  field: {
    marginTop: tokens.spacing.lg,
  },
  fieldLabel: {
    ...tokens.typography.body.small,
    fontWeight: fontWeights.semibold,
    color: role.textStrong,
    marginBottom: tokens.spacing.sm,
  },
  actions: {
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: tokens.spacing.sm,
  },
  confirm: {
    width: '100%',
  },
}));
